import { FormEvent, useState } from 'react';
import InlineScript from '../components/InlineScript';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import { pageUrl } from '../config/site';
import { LOGO_ALT, LOGO_SRC } from '../constants/branding';
import { BEFORE_AFTER } from '../constants/projectGallery';
import Navigation from '../components/navigation';
import Footer from '../components/footer';
import { submitToMailer } from '../lib/submitToMailer';
import './contact.css';

export default function Contact() {
  const [mailStatus, setMailStatus] = useState<
    'idle' | 'sending' | 'ok' | 'err'
  >('idle');
  const [mailMessage, setMailMessage] = useState('');

  async function handleContactSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setMailStatus('sending');
    setMailMessage('');
    const form = e.currentTarget;
    const fd = new FormData(form);
    const fileInput = form.querySelector<HTMLInputElement>('#file-upload');
    const result = await submitToMailer({
      form: 'contact',
      name: String(fd.get('name') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      email: String(fd.get('email') ?? ''),
      service: String(fd.get('service') ?? ''),
      message: String(fd.get('message') ?? ''),
      contactTime: String(fd.get('contact-time') ?? ''),
      freeEstimate: fd.get('free_estimate') === 'yes',
      fileCount: fileInput?.files?.length ?? 0,
    });
    if (result.ok) {
      setMailStatus('ok');
      setMailMessage('Thanks — we received your message and will reply soon.');
      form.reset();
    } else {
      setMailStatus('err');
      setMailMessage(result.error);
    }
  }

  return (
    <div className="contact-container1">
      <Helmet>
        <title>Contact | Shiny Touch Painting &amp; Renovation</title>
        <meta
          property="og:title"
          content="Contact | Shiny Touch Painting &amp; Renovation"
        />
        <link rel="canonical" href={pageUrl('/contact')} />
        <meta property="og:url" content={pageUrl('/contact')} />
      </Helmet>
      <Navigation></Navigation>
      <section className="contact-hero">
        <div className="contact-hero-media">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://images.pexels.com/videos/6474138/pictures/preview-0.jpeg"
            src="https://videos.pexels.com/video-files/6474138/6474138-hd_720_1280_25fps.mp4"
            className="contact-hero-video"
          ></video>
          <div className="contact-hero-overlay"></div>
        </div>
        <div className="contact-hero-container">
          <div className="contact-hero-content">
            <div className="contact-hero-brand">
              <div className="contact-hero-logo-wrapper">
                <img
                  src={LOGO_SRC}
                  alt={LOGO_ALT}
                  className="contact-hero-logo"
                  width={160}
                  height={160}
                />
              </div>
              <span className="contact-hero-company">
                Shiny Touch Painting &amp; Renovation Ltd
              </span>
            </div>
            <h1 className="hero-title">Reliable. Affordable. Professional.</h1>
            <p className="hero-subtitle">
              Transforming Burnaby homes with expert interior &amp; exterior
              painting and full renovation services. Get your free estimate
              today.
            </p>
            <div className="contact-hero-actions">
              <a href="#quote-form">
                <div className="btn btn-primary btn-lg">
                  <span>Request a Free Quote</span>
                </div>
              </a>
              <a href="tel:6040000000">
                <div className="contact-hero-phone-btn btn btn-lg btn-outline">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233a14 14 0 0 0 6.392 6.384"></path>
                  </svg>
                  <span>Call Now</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
      <section id="quote-form" className="quick-contact">
        <div className="quick-contact-container">
          <div className="quick-contact-card">
            <div className="quick-contact-header">
              <h2 className="section-title">Get Your Free Estimate</h2>
              <p className="section-content">
                Tell us about your project and we&apos;ll get back to you within
                24 hours.
              </p>
            </div>
            <form className="quick-contact-form" onSubmit={handleContactSubmit}>
              <div className="quick-contact-grid">
                <div className="quick-contact-field">
                  <label htmlFor="name" className="quick-contact-label">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="John Doe"
                    data-form-field-id="name"
                    className="quick-contact-input"
                  />
                </div>
                <div className="quick-contact-field">
                  <label htmlFor="phone" className="quick-contact-label">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    placeholder="(604) 000-0000"
                    data-form-field-id="phone"
                    className="quick-contact-input"
                  />
                </div>
                <div className="quick-contact-field">
                  <label htmlFor="email" className="quick-contact-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    data-form-field-id="email"
                    className="quick-contact-input"
                  />
                </div>
                <div className="quick-contact-field">
                  <label htmlFor="service" className="quick-contact-label">
                    Service Type
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    defaultValue=""
                    data-form-field-id="service"
                    className="quick-contact-input"
                  >
                    <option value="" disabled>
                      Select a service
                    </option>
                    <option value="interior-painting">Interior Painting</option>
                    <option value="exterior-painting">Exterior Painting</option>
                    <option value="full-renovation">Full Renovation</option>
                    <option value="strata-maintenance">
                      Strata Maintenance
                    </option>
                  </select>
                </div>
                <div className="quick-contact-field full-width">
                  <label htmlFor="message" className="quick-contact-label">
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    placeholder="Describe your project..."
                    data-form-field-id="message"
                    className="quick-contact-input"
                  ></textarea>
                </div>
                <div className="quick-contact-field">
                  <label htmlFor="contact-time" className="quick-contact-label">
                    Preferred Contact Time
                  </label>
                  <select
                    id="contact-time"
                    name="contact-time"
                    data-form-field-id="contact-time"
                    className="quick-contact-input"
                  >
                    <option value="anytime">Anytime</option>
                    <option value="morning">Morning (8am - 12pm)</option>
                    <option value="afternoon">Afternoon (12pm - 5pm)</option>
                    <option value="evening">Evening (5pm - 8pm)</option>
                  </select>
                </div>
                <div className="quick-contact-field">
                  <label htmlFor="file-upload" className="quick-contact-label">
                    Upload Before/After Images
                  </label>
                  <div className="quick-contact-file-wrapper">
                    <input
                      type="file"
                      id="file-upload"
                      name="project_images"
                      multiple
                      accept="image/*"
                      data-form-field-id="file-upload"
                      className="quick-contact-file-input"
                    />
                    <div className="quick-contact-file-ui">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 3v12m5-7l-5-5l-5 5m14 7v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      <span>Select Files</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="quick-contact-checkbox-group">
                <label className="quick-contact-checkbox-label">
                  <input
                    type="checkbox"
                    name="free_estimate"
                    value="yes"
                    defaultChecked
                    id="contact-free-estimate"
                    data-form-field-id="contact-free-estimate"
                  />
                  <span className="quick-contact-checkbox-text">
                    I would like to request a free on-site estimate
                  </span>
                </label>
              </div>
              {mailMessage ? (
                <p
                  className="section-content"
                  style={{
                    marginBottom: '1rem',
                    color:
                      mailStatus === 'ok'
                        ? 'var(--color-primary)'
                        : 'var(--color-accent)',
                  }}
                  role="status"
                >
                  {mailMessage}
                </p>
              ) : null}
              <button
                type="submit"
                id="contact-form-submit"
                name="button"
                data-form-field-id="contact-form-submit"
                className="quick-contact-submit btn btn-primary btn-xl"
                disabled={mailStatus === 'sending'}
              >
                {mailStatus === 'sending' ? 'Sending…' : 'Send Quote Request'}
              </button>
            </form>
          </div>
        </div>
      </section>
      <section className="contact-info">
        <div className="contact-info-container">
          <div className="contact-info-grid">
            <div className="contact-info-card">
              <div className="contact-info-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <h3 className="section-subtitle">Our Location</h3>
              <address className="contact-info-address">
                <span>
                  {' '}
                  Shiny Touch Painting &amp; Renovation Ltd
                  <span
                    dangerouslySetInnerHTML={{
                      __html: ' ',
                    }}
                  />
                </span>
                <br></br>
                <span>
                  {' '}
                  99632 Market Crossing PO
                  <span
                    dangerouslySetInnerHTML={{
                      __html: ' ',
                    }}
                  />
                </span>
                <br></br>
                <span>
                  {' '}
                  Burnaby, BC V5J 0H7
                  <span
                    dangerouslySetInnerHTML={{
                      __html: ' ',
                    }}
                  />
                </span>
              </address>
              <a
                href="https://maps.google.com"
                target="_blank"
                rel="noreferrer noopener"
              >
                <div className="btn-link">
                  <span>Get Directions</span>
                </div>
              </a>
            </div>
            <div className="contact-info-card">
              <div className="contact-info-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233a14 14 0 0 0 6.392 6.384"></path>
                </svg>
              </div>
              <h3 className="section-subtitle">Direct Contact</h3>
              <div className="contact-info-links">
                <a href="tel:+17788280572">
                  <div className="contact-info-link">
                    <span>(778) 828-0572</span>
                  </div>
                </a>
                <a href="mailto:info@shinytouch.ca?subject=">
                  <div className="contact-info-link">
                    <span>info@shinytouch.ca</span>
                  </div>
                </a>
              </div>
              <p className="contact-info-note">
                Expected response time: Under 24 hours
              </p>
            </div>
            <div className="contact-info-card">
              <div className="contact-info-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
              </div>
              <h3 className="section-subtitle">Business Hours</h3>
              <ul className="contact-info-hours">
                <li>
                  <span>Mon - Fri:</span>
                  <span>8:00 AM - 6:00 PM</span>
                </li>
                <li>
                  <span>Saturday:</span>
                  <span>9:00 AM - 4:00 PM</span>
                </li>
                <li>
                  <span>Sunday:</span>
                  <span>Closed</span>
                </li>
              </ul>
              <span className="contact-info-status">Currently Open</span>
            </div>
          </div>
        </div>
      </section>
      <section className="location-map">
        <div className="location-map-grid">
          <div className="location-map-content">
            <div className="location-map-text">
              <h2 className="section-title">
                Serving Burnaby &amp; Greater Vancouver
              </h2>
              <p className="section-content">
                We provide professional painting and renovation services across
                Burnaby, Vancouver, New Westminster, and surrounding areas. Our
                mobile teams are ready to visit your property for a free
                estimate.
              </p>
              <div className="location-map-features">
                <div className="location-map-feature">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11l3 3L22 4"></path>
                  </svg>
                  <span>Interior &amp; Exterior Experts</span>
                </div>
                <div className="location-map-feature">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11l3 3L22 4"></path>
                  </svg>
                  <span>Strata Maintenance Specialists</span>
                </div>
                <div className="location-map-feature">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11l3 3L22 4"></path>
                  </svg>
                  <span>Full Renovation Solutions</span>
                </div>
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&amp;destination=99632+Market+Crossing+PO+Burnaby+BC+V5J+0H7"
                target="_blank"
                rel="noreferrer noopener"
              >
                <div className="btn btn-lg btn-secondary">
                  <span>Get Directions</span>
                </div>
              </a>
            </div>
          </div>
          <div className="location-map-frame-wrapper">
            <div className="location-map-overlay">
              <div className="location-map-badge">
                <span>Primary Service Area</span>
              </div>
            </div>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2605.670267205423!2d-122.98188182343883!3d49.19734187138038!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548677c77c000001%3A0x867373f733f33f33!2sMarket%20Crossing!5e0!3m2!1sen!2sca!4v1710000000000!5m2!1sen!2sca"
              width="100%"
              height="100%"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Shiny Touch service area map"
              className="contact-location-map-iframe location-map-iframe"
            ></iframe>
          </div>
        </div>
      </section>
      <section className="testimonials">
        <div className="testimonials-container">
          <div className="testimonials-header">
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-content">
              Trusted by homeowners and strata managers across Burnaby.
            </p>
          </div>
          <div id="testimonialsCarousel" className="testimonials-carousel">
            <div className="contact-testimonials-track">
              <div className="contact-testimonial-card">
                <div className="testimonial-quote-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2zM5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path>
                  </svg>
                </div>
                <p className="contact-testimonial-text">
                  &quot;Shiny Touch did an incredible job on our exterior
                  painting. They were reliable, professional, and the finish is
                  flawless. Highly recommended for any renovation work!&quot;
                </p>
                <div className="contact-testimonial-author">
                  <div className="contact-testimonial-rating">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                  </div>
                  <span className="testimonial-name">
                    Sarah M., Burnaby Homeowner
                  </span>
                </div>
              </div>
              <div className="contact-testimonial-card">
                <div className="testimonial-quote-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2zM5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path>
                  </svg>
                </div>
                <p className="contact-testimonial-text">
                  &quot;As a strata manager, I need contractors who are reliable
                  and affordable. Shiny Touch has been our go-to for property
                  maintenance for 3 years. Excellent service every time.&quot;
                </p>
                <div className="contact-testimonial-author">
                  <div className="contact-testimonial-rating">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                  </div>
                  <span className="testimonial-name">
                    David L., Strata Manager
                  </span>
                </div>
              </div>
              <div className="contact-testimonial-card">
                <div className="testimonial-quote-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2zM5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path>
                  </svg>
                </div>
                <p className="contact-testimonial-text">
                  &quot;Professional from start to finish. They renovated our
                  kitchen and bathroom within budget and on time. The
                  workmanship is truly top-tier.&quot;
                </p>
                <div className="contact-testimonial-author">
                  <div className="contact-testimonial-rating">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"></path>
                    </svg>
                  </div>
                  <span className="testimonial-name">
                    James K., Vancouver Resident
                  </span>
                </div>
              </div>
            </div>
            <div className="testimonials-controls">
              <button
                aria-label="Previous testimonial"
                className="testimonials-btn prev"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18l-6-6l6-6"></path>
                </svg>
              </button>
              <button
                aria-label="Next testimonial"
                className="testimonials-btn next"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m9 18l6-6l-6-6"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="gallery-teaser">
        <div className="gallery-teaser-header">
          <h2 className="section-title">Our Workmanship</h2>
          <p className="section-content">
            See the Shiny Touch difference in our recent transformations.
          </p>
        </div>
        <div className="gallery-teaser-carousel">
          <div className="gallery-teaser-track">
            <div className="gallery-teaser-item">
              <div className="gallery-teaser-comparison">
                <div className="gallery-teaser-image-box">
                  <img
                    src={BEFORE_AFTER.project0.before}
                    alt="Before — client project"
                    className="gallery-teaser-img"
                  />
                  <span className="gallery-teaser-label">Before</span>
                </div>
                <div className="gallery-teaser-image-box">
                  <img
                    src={BEFORE_AFTER.project0.after}
                    alt="After — client project"
                    className="gallery-teaser-img"
                  />
                  <span className="gallery-teaser-label">After</span>
                </div>
              </div>
              <div className="gallery-teaser-info">
                <h4 className="section-subtitle">
                  Recent interior transformation
                </h4>
              </div>
            </div>
            <div className="gallery-teaser-item">
              <div className="gallery-teaser-comparison">
                <div className="gallery-teaser-image-box">
                  <img
                    src={BEFORE_AFTER.project1.before}
                    alt="Before — second project"
                    className="gallery-teaser-img"
                  />
                  <span className="gallery-teaser-label">Before</span>
                </div>
                <div className="gallery-teaser-image-box">
                  <img
                    src={BEFORE_AFTER.project1.after}
                    alt="After — second project"
                    className="gallery-teaser-img"
                  />
                  <span className="gallery-teaser-label">After</span>
                </div>
              </div>
              <div className="gallery-teaser-info">
                <h4 className="section-subtitle">
                  Another before &amp; after result
                </h4>
              </div>
            </div>
          </div>
        </div>
        <div className="gallery-teaser-footer">
          <Link to="/before-after-gallery">
            <div className="btn btn-lg btn-outline">
              <span>View Full Before &amp; After Gallery</span>
            </div>
          </Link>
        </div>
      </section>
      <section className="footer-cta">
        <div className="footer-cta-container">
          <div className="footer-cta-card">
            <div className="footer-cta-content">
              <h2 className="section-title">Ready to Start Your Project?</h2>
              <p className="section-content">
                Shiny Touch Painting &amp; Renovation Ltd provides free
                estimates for all residential and strata property projects.
                Reliable, affordable, and professional service is just a click
                away.
              </p>
              <div className="footer-cta-services-summary">
                <div className="footer-cta-tag">
                  <span>Free Estimates</span>
                </div>
                <div className="footer-cta-tag">
                  <span>Strata Maintenance</span>
                </div>
                <div className="footer-cta-tag">
                  <span>Full Renovation</span>
                </div>
              </div>
              <div className="footer-cta-actions">
                <a href="#quote-form">
                  <div className="btn btn-primary btn-xl">
                    <span>Request a Free Quote</span>
                  </div>
                </a>
                <div className="footer-cta-contact-text">
                  <span>Or call us at:</span>
                  <a href="tel:+17788280572">
                    <div className="btn-link">
                      <span>(778) 828-0572</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="contact-container2">
        <div className="contact-container3">
          <InlineScript
            html={`<style>
* {
  transition: background-color 0.3s ease, color 0.3s ease;
}
</style>`}
          ></InlineScript>
        </div>
      </div>
      <div className="contact-container4">
        <div className="contact-container5">
          <InlineScript
            html={`<script defer data-name="shiny-touch-contact">
(function(){
  // Testimonials Carousel Logic
  const track = document.querySelector(".contact-testimonials-track")
  const prevBtn = document.querySelector(".testimonials-btn.prev")
  const nextBtn = document.querySelector(".testimonials-btn.next")

  if (track && prevBtn && nextBtn) {
    let index = 0
    const cards = document.querySelectorAll(".contact-testimonial-card")

    const updateCarousel = () => {
      const cardWidth = cards[0].offsetWidth + 24 // Including gap
      track.style.transform = \`translateX(-\${index * cardWidth}px)\`
    }

    nextBtn.addEventListener("click", () => {
      index = (index + 1) % cards.length
      if (window.innerWidth > 991 && index === cards.length - 1) index = 0
      updateCarousel()
    })

    prevBtn.addEventListener("click", () => {
      index = (index - 1 + cards.length) % cards.length
      updateCarousel()
    })

    window.addEventListener("resize", updateCarousel)
  }

  // File Input Visual Feedback
  const fileInput = document.getElementById("file-upload")
  const fileUI = document.querySelector(".quick-contact-file-ui span")

  if (fileInput && fileUI) {
    fileInput.addEventListener("change", (e) => {
      const count = e.target.files.length
      fileUI.textContent = count > 0 ? \`\${count} file(s) selected\` : "Select Files"
    })
  }

  // Form Validation UX
  const form = document.querySelector(".quick-contact-form")
  if (form) {
    form.querySelectorAll("input, textarea, select").forEach((input) => {
      input.addEventListener("blur", () => {
        if (!input.checkValidity()) {
          input.style.borderColor = "#e74c3c"
        } else {
          input.style.borderColor = "var(--color-border)"
        }
      })
    })
  }

  // Parallax Effect for Hero
  window.addEventListener("scroll", () => {
    const video = document.querySelector(".contact-hero-video")
    if (video) {
      const scroll = window.pageYOffset
      video.style.transform = \`translateY(\${scroll * 0.3}px)\`
    }
  })
})()
</script>`}
          ></InlineScript>
        </div>
      </div>
      <Footer></Footer>
    </div>
  );
}
